import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "./fetchWithAuth";
import { useGlobalStore } from "../context/UserContext/userContext";
import { computeAge, formatStudyData } from "./helper";
import { generatePDFContent } from "./pdfGenerator";
import { AUTH_SOURCE, usesReportingGetUserStudy } from "../constants/authSource";
import { getReportingApiBaseUrl, getStudyApiBaseUrl } from "../config/apiBaseUrls";
import { reportingClient } from "../api/httpClients/reportingClient";

export const useStudyById = (studyId) => {
  return useQuery({
    queryKey: ["studyById", studyId],
    queryFn: () => fetchCurrentStudy(studyId),
    enabled: !!studyId,
  });
};

export const fetchCurrentStudy = async (studyId) => {
  const { authSource } = useGlobalStore.getState();

  if (usesReportingGetUserStudy(authSource)) {
    const url = new URL(`${getReportingApiBaseUrl()}/api/Report/GetUserStudy`);
    const response = await fetchWithAuth(url, {
      method: "POST",
      body: JSON.stringify({}),
    });
    const data = await response.json();
    return formatStudyData(data?.payload);
  }

  const url = new URL(`${getStudyApiBaseUrl()}/api/study/getStudyById`);
  url.searchParams.set("studyId", studyId);
  const response = await fetchWithAuth(url, { method: "GET" });
  const data = await response.json();
  return data.payload;
};

// Fetch user files for both Wix and Radical users
export const getUserFiles = async () => {
  const response = await reportingClient.post("/api/Report/GetUserFiles", {});
  const data = response?.data;
  // Normalize the response to match expected format
  const { isWixAuth, authSource } = useGlobalStore.getState();
  if (isWixAuth || authSource === AUTH_SOURCE.CRE) {
    return normalizeWixFiles(data.payload);
  }
  // For Radical users, normalize to ensure consistent structure
  return normalizeFiles(data.payload || []);
};

/**
 * Normalize file response to ensure consistent structure:
 * Converts file objects to { fileId, url, fileName, type }
 */
export function normalizeFiles(files) {
  if (!Array.isArray(files)) return [];
  return files.map((f) => {
    // Extract type from file name if not present
    const type =
      f.type ||
      (f.fileName || f.name || "")
        .split(".")
        ?.pop()
        ?.toLowerCase() || "";

    return {
      fileId: f.fileId || f.id,
      url: f.url || null,
      fileName: f.fileName || f.name || "",
      type: type,
      documentType: f.documentType || f.document_type || null,
    };
  });
}

/**
 * Normalize Wix file response to match non-Wix format:
 * Converts Wix file objects to { fileId, url, fileName, type }
 */
export function normalizeWixFiles(wixFiles) {
  if (!Array.isArray(wixFiles)) return [];
  return wixFiles.map((f) => {
    // Extract type from file name if not present
    const type =
      f.name
        .split(".")
        ?.pop()
        ?.toLowerCase() || "";

    return {
      fileId: f.fileId,
      url: f.url || null,
      fileName: f.name || "",
      type,
      documentType: f.documentType || f.document_type || null,
    };
  });
}

export const updateUserStudy = async (updateObj) => {
  const response = await reportingClient.post("/api/Report/UpdateUserStudy", updateObj);
  const data = response?.data;
  if (data?.success && data?.payload) return formatStudyData(data.payload);
  return null;
};

export const deleteUserFiles = async (fileIds) => {
  const response = await reportingClient.post("/api/Report/DeleteFiles", { fileIds });
  return response?.data;
};

// Upload Files - Works for both Wix and Radical users
export const uploadFiles = async (files, documentType) => {
  if (!files || files.length === 0) throw new Error("No files to upload");
  if (!documentType) throw new Error("document_type is required");

  const endpoint = "/api/Report/UploadFiles";
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("Files", file);
  });
  formData.append("document_type", documentType);
  try {
    const response = await reportingClient.post(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      maxBodyLength: Infinity,
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading files:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

export const getScannedDocsSummary = async () => {
  const response = await reportingClient.post("/api/Report/GetScannedDocsSummary", {});
  return response?.data;
};

// Get User Credits (Wix)
export const getUserCredits = async () => {
  const response = await reportingClient.post("/api/Report/GetUserCredits", {});
  return response?.data;
};

// Reset user data for a fresh report session (Wix/Reporting)
export const resetUserData = async () => {
  const response = await reportingClient.post("/api/Report/ResetUserData", {});
  return response?.data;
};

// Send Report to Wix endpoint (medilis.ai)
export const sendWixReport = async (payload) => {
  const url = new URL("https://www.medilis.ai/_functions/sendReport");
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  // Try parsing JSON; if not JSON, throw the raw text for debugging
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error(text || "Failed to parse sendReport response");
  }
};

// Build payload from patientDetails + wix token and call sendWixReport
export const sendWixReportFromPatientDetails = async (
  transcriptContent = "",
  studyData = null,
  language = "en"
) => {
  const { wixToken } = useGlobalStore.getState();
  if (!wixToken) {
    throw new Error("Missing Wix token");
  }

  // Fetch header and footer content
  let headerContent = "";
  let footerContent = "";

  try {
    const { getClientHeader, getClientFooter } = await import("./API_SERVICE");
    const headerResponse = await getClientHeader({}, wixToken);
    const footerResponse = await getClientFooter({}, wixToken);

    if (headerResponse.success) {
      headerContent = headerResponse.payload.content || "";
    }
    if (footerResponse.success) {
      footerContent = footerResponse.payload.content || "";
    }
  } catch (error) {
    console.error("Error fetching header/footer:", error);
    // Continue with empty header/footer if fetch fails
  }

  // Process header and footer using the same template processor logic
  let processedHeader = headerContent;
  let processedFooter = footerContent;

  if (studyData) {
    const catalogMapping = {
      "[firstName]": studyData.patientName
        ? studyData.patientName?.split(" ")[0]
        : "",
      "[lastName]": studyData.patientName
        ? studyData.patientName?.split(" ")[1]
        : "",
      "[mrn]": studyData.modalityCode || "",
      "[patientId]": studyData.patientId || "",
      "[accNo]": studyData.accessionNo || "",
      "[age]": studyData.dateOfBirth ? computeAge(studyData.dateOfBirth) : "",
      "[gender]": studyData.patientSex || "",
      "[dateOfBirth]": studyData.dateOfBirth || "",
      "[weight]": studyData.weight?.toString() || "",
      "[insuranceName]": studyData.insuranceName || "",
      "[insuranceNumber]": studyData.insuranceNumber || "",
    };

    if (headerContent) {
      processedHeader = headerContent;
      for (const [placeholder, value] of Object.entries(catalogMapping)) {
        processedHeader = processedHeader.split(placeholder).join(value);
      }
    }

    if (footerContent) {
      processedFooter = footerContent;
      for (const [placeholder, value] of Object.entries(catalogMapping)) {
        processedFooter = processedFooter.split(placeholder).join(value);
      }
    }
  }

  // Fetch last recorded video and intro video for PDF
  let lastVideoUrl = null;
  let introVideoUrl = null;
  try {
    const { getVideos } = await import("./API_SERVICE_VIDEO");
    if (studyData?.studyId && wixToken) {
      const videoResponse = await getVideos(studyData.studyId, wixToken);
      if (
        videoResponse.success &&
        videoResponse.payload &&
        Array.isArray(videoResponse.payload)
      ) {
        // Filter out intro videos and get the most recent recorded video
        const recordedVideos = videoResponse.payload
          .filter((video) => !video.isIntro)
          .sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA;
          });

        if (recordedVideos.length > 0 && recordedVideos[0].longUrl) {
          lastVideoUrl = recordedVideos[0].longUrl;
        }

        // Get intro video (there's only one)
        const introVideo = videoResponse.payload.find((video) => video.isIntro);
        if (introVideo && introVideo.longUrl) {
          introVideoUrl = introVideo.longUrl;
        }
      }
    }
  } catch (error) {
    console.error("Error fetching videos for PDF:", error);
  }

  // Generate PDF content using the helper function
  const fileContent = await generatePDFContent(
    processedHeader,
    processedFooter,
    transcriptContent,
    lastVideoUrl,
    introVideoUrl,
    language
  );

  const payload = {
    token: wixToken,
    reportTitle: studyData.modalityCode || "",
    fileContent: fileContent, // Base64 encoded PDF content
    firstName: studyData.patientName?.split(" ")[0] || "",
    lastName: studyData.patientName?.split(" ")[1] || "",
    mrn: studyData.modalityCode || "",
    patientId: studyData.patientId || "",
    accNumber: studyData.accessionNo || "",
    gender: studyData.patientSex || "",
    age: computeAge(studyData.dateOfBirth || ""),
    dateOfBirth: studyData.dateOfBirth || "",
    weight: studyData.weight || "",
    insurance: studyData.insuranceName || "",
    insuranceNumber: studyData.insuranceNumber || "",
  };

  return await sendWixReport(payload);
};
